import { FC, useEffect } from "react";
import { useTranslation } from 'react-i18next';

import { usePreferences } from "../../../utility/usePreferences";
import { DatePicker } from './common/date-picker';
import { DateTimePicker } from './common/date-time-picker';
import { mutateDate, getToday, getYesterday } from './common/date-utils';
import { ICriteria, IDateCriteriaConfig } from "./common/types";
import { Nullable } from "../../../types/utility";
import { useUserParams } from '../../../context/user-params-context';
import { useDate } from "../../../utility/useDate";

export const StartDateSelector: FC<ICriteria<IDateCriteriaConfig>> = ({ config }) => {
  const { userParams, setUserParams, clearParams } = useUserParams();
  const { timeZone } = usePreferences();
  const { localizeDate } = useDate();
  const { t } = useTranslation();

  const today = getToday(timeZone);
  const yesterday = getYesterday(timeZone);
  const tomorrow = mutateDate(today, { add: { days: 1 } });
  const oneWeekBeforeYesterday = mutateDate(yesterday, {
    sub: { days: 6 }
  });

  // Determine if this is for a forecast tab (with unrestricted future dates)
  const isForecasting = config?.isForecast === true;

  const includeTime = config?.includeTime === true;

  // For forecasting: use tomorrow, otherwise use config default date or 7 days before yesterday
  const startDate = isForecasting
    ? tomorrow
    : config?.defaultDate ? mutateDate(today, config.defaultDate) : oneWeekBeforeYesterday;

  // For forecasting: use tomorrow as minDate, otherwise use config min or undefined
  const minDate = isForecasting
    ? tomorrow
    : (config?.minDate ? mutateDate(today, config.minDate) : undefined);

  // For forecasting: don't restrict max date, otherwise use config max or yesterday
  const maxDate = isForecasting
    ? undefined
    : (config?.maxDate ? mutateDate(today, config.maxDate) : yesterday);

  useEffect(() => {
    const localizedDate = localizeDate(startDate.toJSDate()).startOf('day');
    setUserParams({ "@u_startDate": localizedDate.toISO() });

    return () => {
      clearParams(['@u_startDate']);
    };
  }, [config]);

  const handleChangeDate = (value: Nullable<Date>) => {
    if (value) {
      const date = localizeDate(value).startOf('day').toISO();
      setUserParams({ "@u_startDate": date });
    }
  };

  const handleChangeDateTime = (value: Nullable<string>) => {
    if (value) {
      setUserParams({ "@u_startDate": value });
    }
  };

  if (includeTime) {
    return (
      <DateTimePicker
        minDate={minDate?.toJSDate()}
        maxDate={maxDate?.toJSDate()}
        value={userParams["@u_startDate"]}
        onChangeDateTime={handleChangeDateTime}
        label={t("booking.labels.startDate")}
        timeLabel={t("booking.labels.startDate") + " Time"}
        testIdPrefix="start-date"
      />
    );
  }

  return (
    <DatePicker
      minDate={minDate?.toJSDate()}
      maxDate={maxDate?.toJSDate()}
      value={userParams["@u_startDate"]}
      placeholderText={t("booking.labels.startDate")}
      onChangeDate={handleChangeDate}
      label={t("booking.labels.startDate")}
    />
  );
};

export default StartDateSelector;
